import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Card,
    CardContent,
    Divider,
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    CardHeader
} from '@mui/material';
import { useNotification } from 'config/Notification';
import {
    fetchAllStandards as fetchStandardsService,
    saveNewControlByStandard as  saveControlService,
    fetchControlsByStandardId as fetchControlService,
    updateControlsById as updateControlService,
} from 'services/modules/cap/standardservice';
import Breadcrumbs from 'config/Breadcrumb';


const ConfigureControls = () => {

    // Notification
    const { openNotification } = useNotification();

    // State Variables
    const standardTypes = [
        { id: 'standard-based', name: 'Standard Based' },
        { id: 'domain-based', name: 'Domain Based' }
    ];
    const [standardType, setStandardType] = useState(standardTypes[0].id);
    const [selectedStandards, setSelectedStandards] = useState([]);
    const [controls, setControls] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isStep2Editing, setIsStep2Editing] = useState(false);
    const [editControlId, setEditControlId] = useState(null);
    const [openStep2, setOpenStep2] = React.useState(false);
 
    // Fetch standards data based on the selected standard type
    const fetchStandardsData = async () => {
        try {
            const response = await fetchStandardsService(
                standardType === 'standard-based' ? 'Main Standard' : 'Domain Standard'
            );
            setSelectedStandards(response.data);
        } catch (error) {
            openNotification("Error while fetching data", 'error');
        }
    };

    // Fetch controls based on the selected standard
    const fetchControlsDataByStandard = async (standard) => {
        try {
            const response = await fetchControlService({standard});
            setControls(response.data || []);
        } catch (error) {
            openNotification("Error while fetching controls", 'error');
        }
    };   

    // This method handles to change the pages
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
      };
    
      // This method handles to change the rows per page based on user selection
      const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
      };

  
    const validationSchemaStep2 = Yup.object({
        standard: Yup.string().required('Required'),
        controlId: Yup.string().required('Required'),
        slno: Yup.string().required('Required'),
        controlName: Yup.string().required('Required'),     
    });

  // Formik setup for step 2
  const formikStep2 = useFormik({
    initialValues: {
        standardName: '',
        standard: '',
        controlId: '',
        slno:'',
        controlName: '',
        controlDescription: '',
        status:true
    },
    validationSchema: validationSchemaStep2,
    onSubmit: async (values) => {
        try {
            const body = {
                "Standard": values.standardName,
                "ControlID": values.controlId,
                "slno": values.slno,
                "Title": values.controlName,
                "Description": values.controlDescription,
                "status": values.status
            };
    
            if (isStep2Editing) {
                let controlId=editControlId;
                let standarId=values.standard;
                const response = await updateControlService(controlId,standarId,{body});
                if (response.status === 200) {
                    openNotification("Control updated successfully", 'success');
                    fetchControlsDataByStandard(values.standard);
                    let standardName= values.standardName;
                    let standard =values.standard;
                    formikStep2.resetForm();
                    formikStep2.setFieldValue('standardName', standardName);
                    formikStep2.setFieldValue('standard', standard);
                    setIsStep2Editing(false); 
                    setEditControlId(null); 
                }
            } else {
                const response = await saveControlService({ standard: values.standard, body });
                if (response.status === 201) {
                    openNotification("Control saved successfully", 'success');
                    fetchControlsDataByStandard(values.standard);
                    let standardName= values.standardName;
                    let standard =values.standard;
                    formikStep2.resetForm();
                    formikStep2.setFieldValue('standardName', standardName);
                    formikStep2.setFieldValue('standard', standard);
                }
            }
        } catch (error) {
            openNotification("Error while saving control", 'error');
        }
    } 
  });

// Handles edit control 
const handleEditControlClick = (control) => {
    setIsStep2Editing(true);
    setEditControlId(control.id);
    formikStep2.setFieldValue('controlId', control.ControlID);
    formikStep2.setFieldValue('slno', control.slno);
    formikStep2.setFieldValue('controlName', control.Title);
    formikStep2.setFieldValue('controlDescription', control.Description ?? '');
    formikStep2.setFieldValue('status', control.status);
};

    useEffect(() => {
        fetchStandardsData();
        formikStep2.resetForm();
        setIsStep2Editing(false);
        setControls([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [standardType]);
    
    // Fetches control by standard
    useEffect(() => {
        if (formikStep2.values.standard) {
            const selectedStandard = selectedStandards.find(
                (standard) => standard.id === formikStep2.values.standard
            );
            
            if (selectedStandard) {
                formikStep2.setFieldValue('standardName', selectedStandard.stdid);
            }
            fetchControlsDataByStandard(formikStep2.values.standard);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikStep2.values.standard]);

    useEffect(()=>{
        if(controls.length>0){
         formikStep2.setFieldValue('slno', (controls.length+1));
        }else{
         formikStep2.setFieldValue('slno', (1));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
     },[controls])
    
    // Handle status change for step 2
    const handleStep2StatusChange = (event) => {
        const newStatus = event.target.value === 'true';
        if (!newStatus) { // If status is being changed to 'Inactive'
          
                setOpenStep2(true); // Open the confirmation dialog
           
        } else {
            formikStep2.setFieldValue('status', true); // Set status to 'Active'
        }
    };
    
    // Handles in active status
    const handleStep2ConfirmInactive = () => {
        formikStep2.setFieldValue('status', false);
        setOpenStep2(false); 
    };

    const handleStep2Close = () => {
        setOpenStep2(false);
    };
    
    const formContainerStyle = {
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'center',
    };

    const tableContainerStyle = {
        marginBottom: '20px',
    };

    const centerCardStyle = {
        textAlign: 'center',
    };
    const dividerStyle = {
        margin: '20px 0',
    };
    const radioGroupStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
    };
    return (
        <div className='main-cont'>
            <Card>
            <CardHeader
            title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                  <Breadcrumbs paths={['CAP Configurator','Configure Master Control']} />
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
                <CardContent>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Select Standard Type</FormLabel>
                            <RadioGroup
                                aria-label="standard-type"
                                name="standardType"
                                value={standardType}
                                onChange={(e) => setStandardType(e.target.value)}
                                style={radioGroupStyle} 
                            >
                                {standardTypes.map((type) => (
                                    <FormControlLabel
                                        key={type.id}
                                        value={type.id}
                                        control={<Radio />}
                                        label={type.name}
                                    />
                                ))}
                            </RadioGroup>
                            <b>Standard Based 70% & Domain Based 30%</b>
                        </FormControl>
                    <Divider style={dividerStyle} />
                    {standardType && (
                        <>
                        
                            <Divider style={dividerStyle} />
                            <CardContent style={centerCardStyle}>
                     
                                    <div style={formContainerStyle}>
                                        <Card className="row company-screen-bg col-md-10">
                                            <CardContent>
                                                <form onSubmit={formikStep2.handleSubmit}>
                                                    <div className="row "> 
                                                    <FormControl className='col-md-6 px-2' margin="normal">
                                                    <InputLabel htmlFor="standard">Standard</InputLabel>
                                                        <Select
                                                            name="standard"
                                                              label="Standard"
                                                            value={formikStep2.values.standard}
                                                            onChange={formikStep2.handleChange}
                                                            error={formikStep2.touched.standard && Boolean(formikStep2.errors.standard)}
                                                        >
                                                            {selectedStandards  && selectedStandards.map((standard) => (
                                                                <MenuItem key={standard.id} value={standard.id}>
                                                                    {standard.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {formikStep2.touched.standard && formikStep2.errors.standard && (
                                                            <div className="error-message">{formikStep2.errors.standard}</div>
                                                        )}
                                                    </FormControl>
                                                    <FormControl className='col-md-6 px-2'  margin="normal">
                                                        <TextField
                                                            label="Control Id"
                                                            name="controlId"
                                                            onChange={formikStep2.handleChange}
                                                            value={formikStep2.values.controlId}
                                                            error={formikStep2.touched.controlId && Boolean(formikStep2.errors.controlId)}
                                                            helperText={formikStep2.touched.controlId && formikStep2.errors.controlId}
                                                        />
                                                    </FormControl>
                                                    </div>
                                               
                                                    <FormControl className='col-md-6 px-2' margin="normal">
                                                        <TextField
                                                            label="Control Name"
                                                            name="controlName"
                                                            onChange={formikStep2.handleChange}
                                                            value={formikStep2.values.controlName}
                                                            error={formikStep2.touched.controlName && Boolean(formikStep2.errors.controlName)}
                                                            helperText={formikStep2.touched.controlName && formikStep2.errors.controlName}
                                                        />
                                                    </FormControl>
                                                    <FormControl className='col-md-6 px-2' margin="normal">
                                                        <TextField
                                                            label="Control Description"
                                                            name="controlDescription"
                                                            onChange={formikStep2.handleChange}
                                                            value={formikStep2.values.controlDescription}
                                                            error={formikStep2.touched.controlDescription && Boolean(formikStep2.errors.controlDescription)}
                                                            helperText={formikStep2.touched.controlDescription && formikStep2.errors.controlDescription}
                                                            multiline
                                                            rows={1} 
                                                            maxRows={Infinity} 
                                                        />
                                                    </FormControl>
                                                  
                                                                 <div className='row col-md-12 px-2 align-center'>
                                                                    <FormControl component="fieldset" className='d-flex' margin="normal">
                                                                        <RadioGroup
                                                                            row
                                                                            aria-label="status"
                                                                            name="status"
                                                                            value={formikStep2.values.status ? 'true' : 'false'}
                                                                            onChange={handleStep2StatusChange}
                                                                        >
                                                                            <FormControlLabel value="true" control={<Radio />} label="Active" />
                                                                            <FormControlLabel value="false" control={<Radio />} label="Inactive" />
                                                                        </RadioGroup>
                                                                    </FormControl>
                                                                </div>
                                                    <div className='dived-card align-center'>
                                                        <Button color="primary" 
                                                        className='mx-3'
                                                        variant="contained" type="submit">
                                                            {isStep2Editing ? 'Update' : 'Save'}
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            className="danger mx-3"
                                                            onClick={() => { 
                                                                let standardName= formikStep2.values.standardName;
                                                                let standard =formikStep2.values.standard;
                                                                formikStep2.resetForm();
                                                                formikStep2.setFieldValue('standardName', standardName);
                                                                formikStep2.setFieldValue('standard', standard);
                                                                formikStep2.setFieldValue('slno', controls.length>0?controls.length+1:1);
                                                                    setIsStep2Editing(false); 
                                                                    setEditControlId(null);
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                    <Dialog
                                                        open={openStep2}
                                                        onClose={handleStep2Close}
                                                        aria-labelledby="alert-dialog-title"
                                                        aria-describedby="alert-dialog-description"
                                                    >
                                                        <DialogTitle id="alert-dialog-title">{"Confirm Inactive Status"}</DialogTitle>
                                                        <DialogContent>
                                                            <DialogContentText id="alert-dialog-description">
                                                            Would you really like to change the status to "Inactive"?.
                                                            </DialogContentText>
                                                        </DialogContent>
                                                        <DialogActions>
                                                            <Button onClick={handleStep2Close} color="primary">
                                                                Cancel
                                                            </Button>
                                                            <Button onClick={handleStep2ConfirmInactive} color="secondary" autoFocus>
                                                                Ok
                                                            </Button>
                                                        </DialogActions>
                                                    </Dialog>
                                                </form>
                                            </CardContent>
                                        </Card>
                                    </div>
                                { controls.length > 0 && (
                                    <div style={tableContainerStyle}>
                                   <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ justifyContent: 'center', textAlign: 'center'}}>Control Id</TableCell>
                                                    <TableCell>Control Name</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {controls.map((control, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell sx={{ justifyContent: 'center', textAlign: 'center'}}>{control.ControlID}</TableCell>
                                                        <TableCell>{control.Title}</TableCell>
                                                        <TableCell style={{ maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                            {control.Description}
                                                        </TableCell>
                                                        <TableCell>{control.status ? 'Active' : 'Inactive'}</TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={() => handleEditControlClick(control)}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                        <TablePagination
                                                rowsPerPageOptions={[10, 25, 100]}
                                                component="div"
                                                count={controls.length}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                onPageChange={handleChangePage}
                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                            />
                                    </div>
                                )}
                                <Divider style={dividerStyle} />
                            </CardContent>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
export default ConfigureControls;
